// DemandesDepart.jsx — Écran président : demandes de départ des membres (valider / refuser).
import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { DoorOpen, Check, X, Loader2, Clock, CheckCircle2, XCircle } from 'lucide-react';

const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '');

export default function DemandesDepart() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/account/departure-requests')
      .then(({ data }) => setRows(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      await api.post(`/account/departure-requests/${id}/${action}`);
      toast.success(action === 'approve' ? 'Demande validée — le compte a été supprimé' : 'Demande refusée');
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setActing(null);
    }
  };

  const pending = rows.filter((r) => r.statut === 'en_attente');
  const treated = rows.filter((r) => r.statut !== 'en_attente');

  return (
    <div className="max-w-3xl" data-testid="demandes-depart-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-2xl bg-[#FFF7ED] grid place-items-center">
          <DoorOpen className="w-6 h-6 text-[#B45309]" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0F172A]">Demandes de départ</h1>
          <p className="text-sm text-[#475569]">Validez ou refusez, en concertation avec le bureau.</p>
        </div>
      </div>

      {loading ? (
        <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>
      ) : (
        <div className="mt-6 space-y-6">
          <section>
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-[#475569] mb-3">En attente ({pending.length})</h2>
            {pending.length === 0 ? (
              <div className="card text-sm text-[#475569]" data-testid="demandes-depart-empty">Aucune demande en attente.</div>
            ) : (
              <div className="space-y-3">
                {pending.map((r) => (
                  <div key={r.id} className="card border border-[#FED7AA] bg-[#FFFBEB]/50" data-testid={`demande-${r.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-display font-bold text-[#0F172A]">{r.user_nom}</div>
                        <div className="text-xs text-[#94A3B8] mb-1">{r.user_email}</div>
                        <div className="text-xs text-[#94A3B8] flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(r.created_at)}</div>
                        {r.motif ? <p className="text-sm text-[#334155] mt-2 bg-white rounded-lg p-2.5 border border-[#F1F5F9]">« {r.motif} »</p> : null}
                      </div>
                      {confirmId === r.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-[#B91C1C]">Supprimer le compte ?</span>
                          <button onClick={() => act(r.id, 'approve')} disabled={acting} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#DC2626] text-white text-sm font-bold disabled:opacity-50" data-testid={`demande-approve-${r.id}`}>
                            {acting === r.id + 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Oui
                          </button>
                          <button onClick={() => setConfirmId(null)} className="px-3 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]">Non</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button onClick={() => setConfirmId(r.id)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#FCA5A5] text-[#B91C1C] text-sm font-semibold hover:bg-[#FEE2E2]" data-testid={`demande-validate-${r.id}`}>
                            <Check className="w-4 h-4" /> Valider le départ
                          </button>
                          <button onClick={() => act(r.id, 'reject')} disabled={acting} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#CBD5E1] text-[#334155] text-sm font-semibold hover:bg-[#F1F5F9] disabled:opacity-50" data-testid={`demande-reject-${r.id}`}>
                            {acting === r.id + 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Refuser
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {treated.length > 0 && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-[#475569] mb-3">Historique</h2>
              <div className="card !p-0 overflow-hidden">
                <ul className="divide-y divide-[#F1F5F9]">
                  {treated.map((r) => (
                    <li key={r.id} className="p-4 flex items-center gap-3">
                      {r.statut === 'validee'
                        ? <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0" />
                        : <XCircle className="w-5 h-5 text-[#94A3B8] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A]">{r.user_nom}</div>
                        <div className="text-xs text-[#94A3B8]">{fmt(r.traitee_le || r.created_at)}</div>
                      </div>
                      <span className={`text-xs font-bold ${r.statut === 'validee' ? 'text-[#16A34A]' : 'text-[#94A3B8]'}`}>
                        {r.statut === 'validee' ? 'Départ validé' : 'Refusée'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
